const MARKER = 'sys9:logout-in-progress';

type LogoutSnapshot = {
  phase: 'open' | 'pending' | 'settled';
  persistenceError: boolean;
};

export function createLogoutCoordinator() {
  let interrupted = false;
  let persistenceError = false;
  try {
    interrupted =
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem(MARKER) !== null;
  } catch {
    persistenceError = true;
  }
  let snapshot: LogoutSnapshot = {
    phase: interrupted || persistenceError ? 'settled' : 'open',
    persistenceError,
  };
  let pending: Promise<void> | null = null;
  let reconciled = false;
  const refreshes = new Set<Promise<unknown>>();
  const listeners = new Set<() => void>();
  const publish = (next: LogoutSnapshot) => {
    snapshot = next;
    listeners.forEach(listener => listener());
  };
  const blocked = () => snapshot.phase !== 'open';

  return {
    getSnapshot: () => snapshot,
    isBlocked: blocked,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    refresh<T>(operation: () => Promise<T>): Promise<T | undefined> {
      if (blocked()) return Promise.resolve(undefined);
      const operationPromise = operation();
      const tracked = operationPromise.finally(() => refreshes.delete(tracked));
      refreshes.add(tracked);
      return tracked;
    },
    start(
      signOut: () => Promise<unknown>,
      invalidate: () => void
    ): Promise<void> {
      if (pending) return pending;
      interrupted = false;
      try {
        sessionStorage.setItem(MARKER, 'pending');
        persistenceError = false;
      } catch {
        persistenceError = true;
      }
      publish({ phase: 'pending', persistenceError });
      invalidate();
      pending = (async () => {
        await Promise.allSettled([...refreshes]);
        try {
          await signOut();
        } catch (failure) {
          console.warn('Sign-out did not leave this page:', failure);
        } finally {
          pending = null;
          publish({ phase: 'settled', persistenceError });
        }
      })();
      return pending;
    },
    async reconcile(
      pathname: string,
      getIdToken: () => Promise<string | null | undefined>,
      getRefreshToken: () => Promise<string | null | undefined>
    ) {
      if (
        !interrupted ||
        reconciled ||
        persistenceError ||
        pathname !== '/login'
      )
        return;
      reconciled = true;
      try {
        const [id, refresh] = await Promise.all([
          getIdToken(),
          getRefreshToken(),
        ]);
        if (!interrupted || pending || id !== null || refresh !== null) return;
        try {
          sessionStorage.removeItem(MARKER);
        } catch {
          publish({ phase: 'settled', persistenceError: true });
          return;
        }
        interrupted = false;
        publish({ phase: 'open', persistenceError: false });
      } catch {
        return;
      }
    },
  };
}

export const logoutCoordinator = createLogoutCoordinator();
